import React, { useState, useEffect } from "react";
import { authApi } from "../../../auth/auth";
import { userSettingsApi } from "../../../api/usersettings";
import Layout from "../../layout/Layout";

const UserProfilePage = () => {
  const [userData, setUserData] = useState({
    id: "",
    email: "",
    company_name: "",
    notifications_email: "",
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "info",
  });

  useEffect(() => {
    fetchUserData();
    fetchNotificationEmail();
  }, []);

  const fetchNotificationEmail = async () => {
    try {
      const response = await userSettingsApi.getNotificationEmail();
      setUserData(prev => ({
        ...prev,
        notifications_email: response.notifications_email || "",
      }));
    } catch (error) {
      console.error("Failed to fetch notification email:", error);
      showNotification("Failed to load notification settings", "error");
    }
  };

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const userDetails = await authApi.getUserDetails();
      setUserData({
        id: userDetails.id || "",
        email: userDetails.email || "",
        company_name: userDetails.company_name || "",
        notifications_email: userDetails.notifications_email || "",
      });
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      showNotification("Failed to load user profile", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authApi.updateProfile({
        company_name: userData.company_name,
      });
      showNotification("Profile updated successfully", "success");
    } catch (error) {
      console.error("Failed to update profile:", error);
      showNotification(
        error.response?.data?.detail || "Failed to update profile",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showNotification("New passwords do not match", "error");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      showNotification("Password must be at least 8 characters", "error");
      return;
    }

    setPasswordLoading(true);

    try {
      await authApi.changePassword({
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });

      showNotification("Password changed successfully", "success");

      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Failed to change password:", error);
      showNotification(
        error.response?.data?.detail || "Failed to change password",
        "error"
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await userSettingsApi.updateNotificationEmail(userData.notifications_email);
      showNotification("Notification settings updated successfully", "success");
    } catch (error) {
      console.error("Failed to update notification settings:", error);
      showNotification(
        error.response?.data?.detail || "Failed to update notification settings",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = "info") => {
    setNotification({
      show: true,
      message,
      type,
    });

    setTimeout(() => {
      setNotification((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <h1 className="text-3xl font-bold mb-6">My Profile</h1>

        {notification.show && (
          <div
            className={`mb-6 p-4 rounded-md ${
              notification.type === "error"
                ? "bg-red-100 text-red-700 border border-red-200"
                : notification.type === "success"
                ? "bg-green-100 text-green-700 border border-green-200"
                : "bg-blue-100 text-blue-700 border border-blue-200"
            }`}
          >
            {notification.message}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Profile Information</h2>

          <form onSubmit={handleProfileSubmit}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={userData.email}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                />
              </div>

              <div>
                <label
                  htmlFor="company_name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Company Name
                </label>
                <input
                  type="text"
                  id="company_name"
                  name="company_name"
                  value={userData.company_name}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-4 py-2 rounded-md text-white font-medium ${
                    loading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Change Password</h2>

          <form onSubmit={handlePasswordSubmit}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="oldPassword"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Current Password
                </label>
                <input
                  type="password"
                  id="oldPassword"
                  name="oldPassword"
                  value={passwordData.oldPassword}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  New Password
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Password must be at least 8 characters
                </p>
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Confirm New Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  className={`w-full px-4 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                    passwordData.newPassword !== passwordData.confirmPassword &&
                    passwordData.confirmPassword !== ""
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {passwordData.newPassword !== passwordData.confirmPassword &&
                  passwordData.confirmPassword !== "" && (
                    <p className="mt-1 text-sm text-red-600">
                      Passwords don't match
                    </p>
                  )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className={`px-4 py-2 rounded-md text-white font-medium ${
                    passwordLoading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {passwordLoading ? "Changing Password..." : "Change Password"}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Notification Settings</h2>

          <form onSubmit={handleNotificationSubmit}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="notifications_email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Notifications Email
                </label>
                <input
                  type="email"
                  id="notifications_email"
                  name="notifications_email"
                  value={userData.notifications_email}
                  onChange={handleProfileChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter email for notifications"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Leave empty to use your primary email
                </p>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-4 py-2 rounded-md text-white font-medium ${
                    loading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {loading ? "Saving..." : "Save Notification Settings"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default UserProfilePage;
